/**
 * One accordion row in the Problems tab: severity icon + reference +
 * shortSummary collapsed; expanding lazy-loads the full analysis
 * (localStorage-cached) and offers "Open analysis".
 */

import { useEffect, useState } from 'react';
import type { ProblemListEntry, VariantAnalysis } from '@skjv/common';
import { fetchVerseAnalysis } from '@/api/verse-analysis';
import { ChevronRightIcon, LoadingSpinner } from '@components/icons';
import { severityFor } from '@components/verse-analysis-panel';

interface ProblemAccordionItemProps {
  problem: ProblemListEntry;
  isExpanded: boolean;
  onToggle: () => void;
  onOpenAnalysis: () => void;
}

export function ProblemAccordionItem({
  problem,
  isExpanded,
  onToggle,
  onOpenAnalysis,
}: ProblemAccordionItemProps) {
  const [analysis, setAnalysis] = useState<VariantAnalysis | null>(null);
  const severity = severityFor(problem.score);

  useEffect(() => {
    if (!isExpanded || analysis) return;
    void fetchVerseAnalysis({
      testamentIndex: problem.testament - 1,
      bookIndex: problem.book - 1,
      chapterIndex: problem.chapter - 1,
      verseIndex: problem.verse - 1,
    }).then((verdict) => {
      if (verdict?.verdict === 'problem') setAnalysis(verdict.analysis);
    });
  }, [isExpanded, analysis, problem]);

  return (
    <div className={`problem-item ${severity.className}`}>
      <button className="problem-item-header" onClick={onToggle} aria-expanded={isExpanded}>
        <severity.Icon className="problem-item-icon" filled />
        <span className="problem-item-text">
          <span className="problem-item-ref">{problem.reference}</span>
          <span className="problem-item-summary">{problem.shortSummary}</span>
        </span>
        <ChevronRightIcon className={`problem-item-chevron ${isExpanded ? 'expanded' : ''}`} />
      </button>

      {isExpanded && (
        <div className="problem-item-body">
          {!analysis ? (
            <LoadingSpinner className="h-4 w-4" />
          ) : (
            <>
              <p className="problem-item-detail">{analysis.summary}</p>
              <p className="problem-item-numbers">{analysis.numberSummary}</p>
            </>
          )}
          <button className="problem-item-open" onClick={onOpenAnalysis}>
            Open analysis
          </button>
        </div>
      )}
    </div>
  );
}
